import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, toTitleCase } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { BulkEquipmentEditDialog } from '@/components/equipment/BulkEquipmentEditDialog';
import type { BulkResult } from '@/components/common/BulkFieldEditDialog';
import { Wrench, Plus, MoreHorizontal, Pencil, Trash2, Clock, X, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
  ConfirmDialog,
} from '@/components/common';
import {
  Equipment as EquipmentType,
  CreateEquipmentDto,
  QueryEquipmentParams,
  EquipmentCategory,
  EquipmentStatus,
  EquipmentCondition,
} from '@/api/services/equipment.api';
import {
  useEquipmentList,
  useEquipmentDashboard,
  useCreateEquipment,
  useUpdateEquipment,
  useDeleteEquipment,
  useLocationsList,
} from '@/api/hooks';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { BAND_STYLE, criticalityLabel } from '@/lib/criticality';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

const categories: EquipmentCategory[] = [
  'DRILLING',
  'COMPLETION',
  'PRODUCTION',
  'TESTING',
  'SAFETY',
  'TRANSPORTATION',
  'OTHER',
];

const statuses: EquipmentStatus[] = [
  'AVAILABLE',
  'IN_USE',
  'MAINTENANCE',
  'REPAIR',
  'RETIRED',
  'DISPOSED',
];

const conditions: EquipmentCondition[] = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'CRITICAL'];

interface EquipmentFormData {
  assetNumber: string;
  name: string;
  description: string;
  category: EquipmentCategory;
  manufacturer: string;
  model: string;
  serialNumber: string;
  locationId: string;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  purchaseDate: string;
  purchasePrice: string;
  warrantyExpiry: string;
  operatingHours: string;
}

const emptyFormData: EquipmentFormData = {
  assetNumber: '',
  name: '',
  description: '',
  category: 'OTHER',
  manufacturer: '',
  model: '',
  serialNumber: '',
  locationId: '',
  status: 'AVAILABLE',
  condition: 'GOOD',
  purchaseDate: '',
  purchasePrice: '',
  warrantyExpiry: '',
  operatingHours: '',
};

export default function Equipment() {
  const navigate = useNavigate();
  const [page] = useState(1);

  // Filters
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Selected ids survive a filter change but their rows do not, which would
  // leave the bar offering to edit assets that are no longer on screen.
  useEffect(() => {
    setSelectedIds([]);
  }, [searchQuery, statusFilter, categoryFilter]);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item?: EquipmentType;
  }>({ open: false });

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EquipmentType | null>(null);
  const [formData, setFormData] = useState<EquipmentFormData>(emptyFormData);

  // Build query params
  const queryParams: QueryEquipmentParams = {
    page,
    limit: 20,
    ...(searchQuery && { search: searchQuery }),
    ...(statusFilter && { status: statusFilter as EquipmentStatus }),
    ...(categoryFilter && { category: categoryFilter as EquipmentCategory }),
  };

  // React Query hooks for data fetching
  const { data: equipmentData, isLoading: loading } = useEquipmentList(queryParams);
  const { data: dashboardData } = useEquipmentDashboard();
  const { data: locationsData } = useLocationsList({ limit: 500 });

  // Derived data from queries
  const equipment = equipmentData?.data?.data ?? [];
  const dashboard = dashboardData?.data ?? null;
  const locations = locationsData?.data ?? [];

  // AI Insights

  // Mutations
  const createMutation = useCreateEquipment();
  const updateMutation = useUpdateEquipment();
  const deleteMutation = useDeleteEquipment();

  const handleSubmit = async () => {
    if (!formData.assetNumber || !formData.name || !formData.locationId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const dto: CreateEquipmentDto = {
      assetNumber: formData.assetNumber,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category,
      manufacturer: formData.manufacturer || undefined,
      model: formData.model || undefined,
      serialNumber: formData.serialNumber || undefined,
      locationId: formData.locationId,
      status: formData.status,
      condition: formData.condition,
      purchaseDate: formData.purchaseDate || undefined,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
      warrantyExpiry: formData.warrantyExpiry || undefined,
      operatingHours: formData.operatingHours ? parseFloat(formData.operatingHours) : undefined,
    };

    if (editingItem) {
      updateMutation.mutate(
        { id: editingItem.id, data: dto },
        {
          onSuccess: () => {
            toast.success('Equipment updated successfully');
            setFormOpen(false);
            setFormData(emptyFormData);
            setEditingItem(null);
          },
        }
      );
    } else {
      createMutation.mutate(dto, {
        onSuccess: () => {
          toast.success('Equipment created successfully');
          setFormOpen(false);
          setFormData(emptyFormData);
          setEditingItem(null);
        },
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;

    deleteMutation.mutate(deleteDialog.item.id, {
      onSuccess: () => {
        toast.success('Equipment deleted successfully');
        setDeleteDialog({ open: false });
      },
    });
  };

  const columns: Column<EquipmentType>[] = [
    {
      key: 'asset',
      linkTo: (row) => `/equipment/${row.id}`,
      header: 'Asset',
      sortable: true,
      // 'asset' is a display id, not a field — sort by the name shown.
      accessorFn: (row) => row.name,
      render: (row: EquipmentType) => (
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.assetNumber}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (row: EquipmentType) => <span className="text-sm">{toTitleCase(row.category)}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      accessorFn: (row) => row.location.name,
      render: (row: EquipmentType) => <span>{row.location.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: EquipmentType) => <StatusBadge status={row.status} />,
    },
    {
      key: 'condition',
      header: 'Condition',
      sortable: true,
      render: (row: EquipmentType) => (
        <StatusBadge status={row.condition.toLowerCase()} label={row.condition} />
      ),
    },
    {
      // Three things this list has never shown, all of them already known:
      // what it costs when the asset fails, whether it can legally go out, and
      // whether it is even here. Somebody scanning to decide what to send
      // somewhere needs all three.
      key: 'criticality',
      header: 'Criticality',
      sortable: true,
      accessorFn: (row: EquipmentType) => row.criticality?.score ?? -1,
      render: (row: EquipmentType) =>
        row.criticality ? (
          <Badge
            variant="outline"
            className={BAND_STYLE[row.criticality.band].className}
            title={row.criticality.provenance}
          >
            {criticalityLabel(row.criticality)}
          </Badge>
        ) : null,
    },
    {
      key: 'certification',
      header: 'Certificates',
      sortable: true,
      accessorFn: (row: EquipmentType) => row.certification?.blocking ?? 0,
      render: (row: EquipmentType) => {
        const c = row.certification;
        if (!c) return <span className="text-xs text-muted-foreground">In date</span>;
        if (c.blocking > 0)
          return (
            <div>
              <Badge className="bg-status-destructive-subtle text-status-destructive-foreground">
                {c.blocking} blocking
              </Badge>
              <p className="mt-1 text-xs text-muted-foreground">
                {[c.missing && `${c.missing} never obtained`, c.lapsed && `${c.lapsed} lapsed`]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </div>
          );
        return (
          <Badge className="bg-status-warning-subtle text-status-warning-foreground">
            {c.expiringSoon} expiring
          </Badge>
        );
      },
    },
    {
      key: 'deployedTo',
      header: 'Where',
      sortable: true,
      accessorFn: (row: EquipmentType) => row.deployedTo?.jobNumber ?? '',
      render: (row: EquipmentType) =>
        row.deployedTo ? (
          <Link
            to={`/jobs/${row.deployedTo.id}`}
            className="text-sm underline-offset-4 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.deployedTo.jobNumber}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">With us</span>
        ),
    },
    {
      key: 'nextServiceDate',
      header: 'Next Service',
      sortable: true,
      render: (row: EquipmentType) =>
        row.nextServiceDate ? (
          <span>{formatDate(row.nextServiceDate)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: EquipmentType) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/equipment/${row.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, item: row })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Equipment' }]}
          title="Equipment"
          description="Owned and leased assets, with condition, criticality, and the service next due on each."
          actions={
            <Button onClick={() => navigate('/equipment/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Add Equipment
            </Button>
          }
        />
      </motion.div>

      {/* Dashboard Metrics */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Equipment"
            value={dashboard?.total || 0}
            sentiment="info"
            subtitle={`${dashboard?.byStatus?.AVAILABLE || 0} available`}
            icon={<Wrench className="h-5 w-5" />}
          />
          <MetricCard
            label="In Use"
            value={dashboard?.byStatus?.IN_USE || 0}
            sentiment="success"
            subtitle={`${dashboard?.utilizationRate || 0}% utilization`}
            icon={<Activity className="h-5 w-5" />}
          />
          <MetricCard
            label="Maintenance Due"
            value={dashboard?.maintenanceDue || 0}
            sentiment={(dashboard?.maintenanceDue || 0) > 0 ? 'warning' : 'success'}
            subtitle="Within 7 days"
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="In Repair"
            value={(dashboard?.byStatus?.REPAIR || 0) + (dashboard?.byStatus?.MAINTENANCE || 0)}
            sentiment="neutral"
            subtitle="Currently unavailable"
            icon={<Wrench className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {/* Data Table */}
      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search equipment..."
              filters={[
                {
                  id: 'status',
                  label: 'Status',
                  value: statusFilter,
                  options: statuses.map((status) => ({
                    value: status,
                    label: status.replace('_', ' '),
                  })),
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
                {
                  id: 'category',
                  label: 'Categories',
                  value: categoryFilter,
                  options: categories.map((cat) => ({
                    value: cat,
                    label: cat.replace('_', ' '),
                  })),
                  onChange: setCategoryFilter,
                  placeholder: 'Category',
                },
              ]}
            />
          }
          columns={columns}
          data={equipment}
          loading={loading}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowId={(row) => row.id}
          onRowClick={(row) => navigate(`/equipment/${row.id}`)}
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} asset{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => setBulkEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit fields
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <BulkEquipmentEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        equipmentIds={selectedIds}
        locations={locations}
        onApplied={(result: BulkResult) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
          // Keep the failures selected so a retry acts on exactly those.
          setSelectedIds(result.results.filter((r) => r.status === 'failed').map((r) => r.id));
        }}
      />

      {/* Create/Edit Form Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingItem ? 'Edit Equipment' : 'Add Equipment'}</SheetTitle>
            <SheetDescription>
              {editingItem ? 'Update equipment details' : 'Add a new piece of equipment'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assetNumber">Asset Number *</Label>
                <Input
                  id="assetNumber"
                  value={formData.assetNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, assetNumber: e.target.value }))
                  }
                  disabled={!!editingItem}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v: EquipmentCategory) =>
                    setFormData((prev) => ({ ...prev, category: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, manufacturer: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData((prev) => ({ ...prev, model: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, serialNumber: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, locationId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v: EquipmentStatus) =>
                    setFormData((prev) => ({ ...prev, status: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Condition</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(v: EquipmentCondition) =>
                    setFormData((prev) => ({ ...prev, condition: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((cond) => (
                      <SelectItem key={cond} value={cond}>
                        {cond}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchaseDate">Purchase Date</Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, purchaseDate: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchasePrice">Purchase Price</Label>
                <Input
                  id="purchasePrice"
                  type="number"
                  value={formData.purchasePrice}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, purchasePrice: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="warrantyExpiry">Warranty Expiry</Label>
                <Input
                  id="warrantyExpiry"
                  type="date"
                  value={formData.warrantyExpiry}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, warrantyExpiry: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="operatingHours">Operating Hours</Label>
                <Input
                  id="operatingHours"
                  type="number"
                  value={formData.operatingHours}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, operatingHours: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingItem
                    ? 'Update'
                    : 'Create'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        title="Delete Equipment"
        description={`Are you sure you want to delete "${deleteDialog.item?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleteMutation.isPending}
      />
    </motion.main>
  );
}
